import type { IFilter } from '@models/Filter'
import { endOfDay, startOfDay } from '@util/date-util'

import { apiClient } from '../../../gateways/api'
import type { ProjectenSearch } from '../types/ProjectenSearch'

export { initialState, createFilters, searchProjecten }

const initialState: ProjectenSearch = {
  data: { count: 0, skip: 0, limit: 25, items: [] },
  query: {
    filters: {
      fromDate: null,
      toDate: null,
      fromGeplandeUitvoeringsdatum: null,
      toGeplandeUitvoeringsdatum: null,
      search: '',
      searchOption: null,
      gebied: [],
      processtap: [],
      active: true,
      netbeheerder: null,
      uitvoerende: null,
      actiehouder: null,
      actie: null,
      labels: [],
      myList: false,
      kpi: '',
      planningsverschil: [],
    },
    sort: { sortBy: 'aanvraagDatum', sortDirection: 'desc' },
    page: { skip: 0, limit: 25 },
  },
}

const createFilters = ({
  fromDate,
  toDate,
  fromGeplandeUitvoeringsdatum,
  toGeplandeUitvoeringsdatum,
  search,
  searchOption,
  gebied,
  processtap,
  active,
  kpi,
  netbeheerder,
  actiehouder,
  actie,
  uitvoerende,
  labels,
  myList,
  planningsverschil,
}: Partial<ProjectenSearch['query']['filters']>) => {
  const filters: IFilter[] = []

  if (fromDate)
    filters.push({
      name: 'aanvraagDatum',
      value: startOfDay(fromDate),
      operator: 'gte',
    })
  if (toDate)
    filters.push({
      name: 'aanvraagDatum',
      value: endOfDay(toDate),
      operator: 'lte',
    })
  if (fromGeplandeUitvoeringsdatum)
    filters.push({
      name: 'geplandeUitvoeringsdatum',
      value: startOfDay(fromGeplandeUitvoeringsdatum),
      operator: 'gte',
    })
  if (toGeplandeUitvoeringsdatum)
    filters.push({
      name: 'geplandeUitvoeringsdatum',
      value: endOfDay(toGeplandeUitvoeringsdatum),
      operator: 'lte',
    })
  if (search && search !== '') {
    if (searchOption)
      filters.push({ name: searchOption.k, value: search, operator: 'eq' })
    else filters.push({ name: 'text', value: search, operator: 'contains' })
  }
  if (gebied && gebied.length > 0)
    filters.push({
      name: 'coordinerende.gebiedscode',
      value: gebied.map(({ code }) => code),
      operator: 'in',
    })
  if (processtap && processtap.length > 0)
    filters.push({ name: 'statusText', value: processtap, operator: 'in' })

  if (active) filters.push({ name: 'isActive', value: active, operator: 'eq' })

  if (kpi) filters.push({ name: 'kpi.rag', value: kpi, operator: 'eq' })
  if (netbeheerder)
    filters.push({ name: 'netbeheerders', value: netbeheerder, operator: 'eq' })

  if (actiehouder)
    filters.push({
      name: 'actiehouders',
      value: actiehouder.label,
      operator: 'eq',
    })
  if (actie)
    filters.push({
      name: `acties.${actie.code}`,
      value: actiehouder
        ? actiehouder.label
        : ({ $exists: true, $not: { $size: 0 } } as any),
      operator: 'eq',
    })

  if (uitvoerende)
    filters.push({ name: 'uitvoerende', value: uitvoerende, operator: 'eq' })

  if (labels && labels.length > 0)
    filters.push({
      name: 'labels',
      value: labels.map(({ code }) => code),
      operator: 'in',
    })

  if (myList) {
    filters.push({ name: 'my-list', value: true, operator: 'eq' })
  }

  if (planningsverschil && planningsverschil.length > 0)
    filters.push({
      name: 'planningsverschillen',
      value: planningsverschil.map((option) => option.code),
      operator: 'in',
    })

  return filters
}

const searchProjecten = async ({
  query,
}: {
  query: ProjectenSearch['query']
}): Promise<ProjectenSearch['data']> => {
  const { filters, page, sort } = query

  const { data } = await apiClient({
    url: '/rest/project/aansluiting/search',
    method: 'POST',
    data: { filters: createFilters(filters), ...page, ...sort },
  })

  return data
}
